import React, { useState, useEffect } from 'react';
import { FiSave, FiRotateCcw, FiSearch, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { userService, leaveAllocationService } from '../../services/api';
import './Admin.css';
import './LeaveAllocation.css';

const LeaveAllocationManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [allocations, setAllocations] = useState({}); // { empId: [ { leaveTypeId, allocatedDays, ... } ] }
  const [saving, setSaving] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await userService.getAll({ limit: 1000 });
      setEmployees(response.data.users || []);
    } catch (err) {
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllocationsForUser = async (userId) => {
    try {
      const response = await leaveAllocationService.getForUser(userId, { year });
      setAllocations(prev => ({ ...prev, [userId]: response.data.allocations }));
    } catch (err) {
      console.error('Failed to fetch allocations:', err);
    }
  };

  const handleExpand = (empId) => {
    if (expandedEmployee === empId) {
      setExpandedEmployee(null);
    } else {
      setExpandedEmployee(empId);
      if (!allocations[empId]) {
        fetchAllocationsForUser(empId);
      }
    }
  };

  const handleDaysChange = (empId, leaveTypeId, value) => {
    setAllocations(prev => {
      const updated = (prev[empId] || []).map(a =>
        a.leaveTypeId === leaveTypeId ? { ...a, allocatedDays: value, isModified: true } : a
      );
      return { ...prev, [empId]: updated };
    });
  };

  const handleNotesChange = (empId, leaveTypeId, value) => {
    setAllocations(prev => {
      const updated = (prev[empId] || []).map(a =>
        a.leaveTypeId === leaveTypeId ? { ...a, notes: value, isModified: true } : a
      );
      return { ...prev, [empId]: updated };
    });
  };

  const handleSave = async (empId) => {
    const empAllocations = allocations[empId];
    if (!empAllocations) return;

    setSaving(empId);
    setError(null);
    setSuccess(null);

    try {
      const payload = empAllocations.map(a => ({
        leaveTypeId: a.leaveTypeId,
        allocatedDays: parseFloat(a.allocatedDays),
        notes: a.notes || '',
      }));

      await leaveAllocationService.updateForUser(empId, { allocations: payload, year });
      await fetchAllocationsForUser(empId);
      setSuccess(`Leave allocation saved for ${employees.find(e => e.id === empId)?.firstName || 'employee'}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save allocations');
    } finally {
      setSaving(null);
    }
  };

  const handleResetToDefault = (empId, leaveTypeId) => {
    setAllocations(prev => {
      const updated = (prev[empId] || []).map(a =>
        a.leaveTypeId === leaveTypeId ? { ...a, allocatedDays: a.defaultDays, isModified: true, notes: '' } : a
      );
      return { ...prev, [empId]: updated };
    });
  };

  const filteredEmployees = employees.filter(emp =>
    `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner"><div className="spinner"></div><p>Loading...</p></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h1>Leave Allocation</h1>
          <p className="header-subtitle">Configure leave days per employee</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      <div className="filters-section">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search employee..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select className="filter-select" value={year} onChange={(e) => {
          setYear(parseInt(e.target.value));
          setAllocations({});
          setExpandedEmployee(null);
        }}>
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      <div className="allocation-list">
        {filteredEmployees.map(emp => {
          const isExpanded = expandedEmployee === emp.id;
          const empAllocs = allocations[emp.id] || [];
          const hasCustom = empAllocs.some(a => a.isCustom);

          return (
            <div key={emp.id} className={`allocation-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="allocation-card-header" onClick={() => handleExpand(emp.id)}>
                <div className="emp-info">
                  <div className="employee-avatar">{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
                  <div>
                    <h3>{emp.firstName} {emp.lastName}</h3>
                    <p>{emp.email}</p>
                  </div>
                </div>
                <div className="header-right">
                  {hasCustom && <span className="custom-badge">Custom</span>}
                  {isExpanded ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                </div>
              </div>

              {isExpanded && (
                <div className="allocation-card-body">
                  {empAllocs.length === 0 ? (
                    <div className="loading-spinner"><div className="spinner"></div></div>
                  ) : (
                    <>
                      <table className="allocation-table">
                        <thead>
                          <tr>
                            <th>Leave Type</th>
                            <th>Default Days</th>
                            <th>Allocated Days</th>
                            <th>Notes</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {empAllocs.map(alloc => (
                            <tr key={alloc.leaveTypeId} className={alloc.isCustom || alloc.isModified ? 'custom-row' : ''}>
                              <td className="type-name">{alloc.leaveTypeName}</td>
                              <td className="default-days">{alloc.defaultDays}</td>
                              <td>
                                <input
                                  type="number"
                                  min="0"
                                  max="365"
                                  step="0.5"
                                  className={`days-input ${parseFloat(alloc.allocatedDays) !== alloc.defaultDays ? 'modified' : ''}`}
                                  value={alloc.allocatedDays}
                                  onChange={(e) => handleDaysChange(emp.id, alloc.leaveTypeId, e.target.value)}
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="notes-input"
                                  placeholder="Optional note..."
                                  value={alloc.notes}
                                  onChange={(e) => handleNotesChange(emp.id, alloc.leaveTypeId, e.target.value)}
                                />
                              </td>
                              <td>
                                {parseFloat(alloc.allocatedDays) !== alloc.defaultDays && (
                                  <button
                                    className="btn-reset"
                                    onClick={() => handleResetToDefault(emp.id, alloc.leaveTypeId)}
                                    title="Reset to default"
                                  >
                                    <FiRotateCcw size={14} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="allocation-actions">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleSave(emp.id)}
                          disabled={saving === emp.id}
                        >
                          <FiSave size={16} />
                          {saving === emp.id ? 'Saving...' : 'Save Allocation'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaveAllocationManagement;
